import Modal from '@/configuration/sources/SimpleModal.yml'
import { getPathValue } from '@/common/util/object'
import Events from '@/configuration/Events'
import {
  isMagento,
  isWooCommerce,
  isPrestashop
} from '@/common/util/environment'

export const getPopinState = () => {
  return {
    isPopinOpen: false,
    modal: {
      isClosing: false,
      isOpen: false,
      loading: false,
      layout: {
        title: '',
        variables: {},
        content: [],
        optional: [],
        buttons: [],
        options: []
      },
      data: {},
      triggers: {
        postClose: {
          action: undefined,
          params: undefined
        }
      },
      selectedOption: null
    },
    // KryptonDialog component
    // list of currently open dialogs, used to check isAnyDialogOpen getter
    dialog: {
      activeDialogs: []
    },
    // Confirmation modal (User gesture required)
    confirmationModal: {
      isOpen: false,
      isClosing: false,
      method: null,
      forceDisplay: false
    },
    iframeModal: {
      isOpen: false,
      isClosing: false,
      url: null
    }
  }
}

export const popinActions = ($locator, app) => {
  return {
    openPopin({ commit, state, getters, dispatch }) {
      if (getters.isFormPopin && !state.isPopinOpen) {
        dispatch('cleanError')
        commit('UPDATE', { isPopinOpen: true })
      }
    },
    closePopin({ dispatch, commit, getters }) {
      const { isAnyPopinOpen } = getters
      if (isAnyPopinOpen) {
        commit('UPDATE', { isPopinOpen: false })
        dispatch('closeSmartFormPopin')
      }
    },
    openModal({ commit, getters }, { layout, ...data }) {
      // Reset Layout, selectedOption, and data
      commit('UPDATE', {
        modal: {
          layout: getPopinState().modal.layout,
          selectedOption: null,
          data: null
        }
      })

      // Build layout partials
      Object.entries(layout).map(([key, value]) => {
        if (value.partial) {
          const partial = Modal.partials[value.partial]
          if (partial) layout[key] = partial
        }
      })

      // Check conditions for optional fields
      const { getLayoutVariable } = getters
      if (Array.isArray(layout.optional) && layout.optional.length > 0) {
        layout.optional = layout.optional.filter(optional => {
          const condition = optional.condition
          return getLayoutVariable(condition, data)
        })
      }

      commit('UPDATE', {
        modal: { isOpen: true, layout, data }
      })
    },
    // Returns a promise that gets resolved once the modal is closed.
    openAwaitableModal(
      {
        dispatch,
        state: {
          modal: { selectedOption }
        }
      },
      { layout, ...data }
    ) {
      return new Promise(resolve => {
        $locator.$bus.$once(Events.krypton.message.closeSimpleModal, () =>
          resolve(selectedOption)
        )
        dispatch('openModal', { layout, ...data })
      })
    },
    /**
     * Starts the closing modal process, there is a delay (for the animation) until the modal really closes
     *
     * postCloseAction and props are OPTIONAL and get called after the modal completely ends the closing animation.
     * The action must be a store action, it cannot be a callback function as it may be called from the ghost and will not be properly synchronized
     */
    closeModal({ commit, getters, dispatch, state }, params = {}) {
      const { postCloseAction, postCloseActionProps } = params

      commit('UPDATE', {
        modal: {
          isClosing: true,
          isOpen: false,
          loading: false,
          triggers: {
            postClose: {
              action: postCloseAction,
              props: postCloseActionProps
            }
          }
        }
      })

      // This error only should be thrown when its closing a modal that starts a payment process of
      // a payment method directly triggered from the popin button (only 1 method available)
      // i.e. the paypal wallet warning modal (see KJS-3744 && KJS-4717)
      if (getters.popinOpensMethodDirectly && !state.smartForm.isOpen) {
        dispatch('error', getters.abortedErrorCode)
      }
    },
    startModalLoading({ commit }) {
      commit('UPDATE', { modal: { loading: true } })
    },
    stopModalLoading({ commit }) {
      commit('UPDATE', { modal: { loading: false } })
    },
    // Action called when the modal is completely closed (after the animation)
    finishModalClosing({ commit, state, dispatch }) {
      const { action, props } = state.modal.triggers.postClose

      // Call the saved action
      if (action) dispatch(action, props)

      // Reset the action for any following modal windows
      commit('UPDATE', {
        modal: {
          triggers: {
            postClose: {
              action: undefined,
              props: undefined
            }
          }
        }
      })

      // Finally emit the event that marks the end of the modal closing process
      const { proxy, storeFactory, $bus } = $locator
      $bus.$emit(Events.krypton.message.closeSimpleModal)
      proxy.send(storeFactory.create('closeSimpleModal'))
    },
    addDialogToActiveList({ commit, state }, component_uid) {
      const updated = [...state.dialog.activeDialogs, component_uid]

      commit('UPDATE', { dialog: { activeDialogs: updated } })
    },
    removeDialogFromActiveList({ commit, state }, component_uid) {
      const updated = state.dialog.activeDialogs.filter(
        comp_uid => component_uid !== comp_uid
      )
      commit('UPDATE', { dialog: { activeDialogs: updated } })
    },
    callModalActions({ commit, state, dispatch }, { actions, name }) {
      commit('UPDATE', { modal: { selectedOption: name } })

      if (!Array.isArray(actions)) actions = [actions]
      actions.forEach(action => dispatch(action, state.modal.data))
    },
    openConfirmationModal(
      { commit, getters, dispatch },
      { method, forceOpen, errorCode }
    ) {
      // Only open if the environment is accepted or if it's forced by the open method options
      // also allow to force in the action for test purposes
      if (getters.shouldDisplayConfirmationModal || forceOpen) {
        // Retry management (remove blocked attempt)
        if (getters.shouldPreventRetry(method))
          dispatch('removePaymentMethodAttempt', method)
        commit('UPDATE', {
          confirmationModal: { isOpen: true, isClosing: false, method }
        })
      } else if (errorCode) {
        dispatch('error', errorCode)
      }
    },
    closeConfirmationModal({ commit }) {
      commit('UPDATE', {
        confirmationModal: { isClosing: true, isOpen: false }
      })
    },
    openIframeModal({ commit }, { url }) {
      commit('UPDATE', {
        iframeModal: { isOpen: true, isClosing: false, url }
      })
    },
    closeIframeModal({ commit }) {
      commit('UPDATE', {
        iframeModal: { isClosing: true, isOpen: false, url: null }
      })
    }
  }
}

export const popinMutations = {}

export const popinGetters = {
  isFormPopin: state => state.formType === 'popin',
  isAnyPopinOpen: state =>
    (state.formType === 'popin' && state.isPopinOpen) ||
    (state.formWidget === 'smartForm' && state.smartForm.isOpen),
  isCardsFormPopin: (state, { isSmartForm, isFormPopin }) =>
    !isSmartForm && isFormPopin,
  isAnyDialogOpen: state => state.dialog.activeDialogs.length !== 0,
  isModalOpen: state =>
    state.modal.isOpen ||
    state.confirmationModal.isOpen ||
    state.iframeModal.isOpen,
  isModalClosing: state =>
    state.modal.isClosing ||
    state.confirmationModal.isClosing ||
    state.iframeModal.isClosing,
  getLayoutVariable:
    (state, { getToken }) =>
    (variable, data) => {
      if (variable?.startsWith('$token')) {
        const path = variable.replace('$token.', '')
        const tokenConf = getToken(data.paymentMethodToken)
        return getPathValue(tokenConf, path)
      }
      if (variable?.startsWith('$state')) {
        const path = variable.replace('$state.', '')
        return getPathValue(state, path)
      }
      if (variable?.startsWith('$modalData')) {
        const path = variable.replace('$modalData.', '')
        return getPathValue(data, path)
      }
    },
  verticalModalButtons: ({ modal }) =>
    !!modal.layout.options?.find(option => option?.verticalButtons)
      ?.verticalButtons,
  shouldDisplayConfirmationModal: ({ confirmationModal, isCMS }) =>
    isCMS ||
    isWooCommerce() ||
    isMagento() ||
    isPrestashop() ||
    confirmationModal.forceDisplay
}
